import React, { useMemo } from "react";
import CustomTable from "components/Table/CustomTable";
import dayjs from "dayjs";
import { LinksSchema } from "model/transactions/links.schema";
import { formatCurrency } from "utils/formatters";
import Image from "components/Common/Image";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";
import useTableChangeHandler from "hooks/useTableChangeHandler";

const EarningPartnerTable = ({ data, onTableChange, error, isLoading }) => {
  const { handleSort, handleFilter, handleSearch, handlePageChange } = useTableChangeHandler(onTableChange);

  const columns = useMemo(
    () => [
      {
        header: "ข้อเสนอ",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="product-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.product.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.product.name} />
            <div>
              <div className="text-lg fw-semibold">{item.product.name}</div>
              <div className="text-md text-gray-500">SKU: {item.product.sku}</div>
            </div>
          </div>
        ),
      },
      {
        header: "ชื่อแคมเปญ",
        accessor: "campaign.name",
        responsive: "md",
        render: (item) => item.campaign?.name || "-",
      },
      {
        header: "วันที่สร้างลิงก์",
        accessor: "createdAt",
        responsive: "lg",
        render: (item) => dayjs(item.createdAt).format("YYYY-MM-DD"),
      },
      {
        header: "จำนวนการคลิก",
        accessor: "stats.clicks",
        sortable: true,
      },
      {
        header: "Unique Clicks",
        accessor: "stats.uniques",
        sortable: true,
      },
      {
        header: "คำสั่งซื้อ",
        accessor: "stats.orders",
        sortable: true,
      },
      {
        header: "โบนัส",
        accessor: "stats.bonus",
        sortable: true,
        align: "end",
        render: (item) => <div className="fw-semibold">{formatCurrency(item.stats.bonus)}</div>,
      },
      {
        header: "คอมมิชชันที่ได้",
        accessor: "stats.commission",
        sortable: true,
        align: "end",
        render: (item) => <div className="fw-semibold">{formatCurrency(item.stats.commission)}</div>,
      },
      {
        header: "รายได้",
        accessor: "stats.earnings",
        sortable: true,
        align: "end",
        render: (item) => <div className="fw-semibold text-primary-600">{formatCurrency(item.stats.earnings)}</div>,
      },
    ],
    []
  );

  return (
    <div>
      <div className="text-lg mb-4">รายการธุรกรรมในเดือนทั้งหมด "{data?.pagination?.total?.toLocaleString() || 0} รายการ"</div>

      <CustomTable
        //
        keyField="id"
        columns={columns}
        data={data?.data || []}
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"}
        errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}
        emptyMessage="ไม่พบข้อมูลธุรกรรม"
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    try {
      if (!props.isLoading && !props.error && props.data) {
        schema.validateSync(props.data);
      }
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(EarningPartnerTable, LinksSchema);

export default ValidatedTable;
